import styled from '@emotion/styled';
import {
  duration,
  easing,
  palette,
  radius,
  shadow,
  space,
  spacing,
  typography,
} from '../../../style-utils/theme';

/**
 * SIGNATURE PAD — styles (plan S6). The field parts come from the form kit;
 * this file adds the pad, the typed preview and the hint line.
 *
 * The pad is 160px tall and as wide as its column up to 560px. Its canvas
 * takes touch-action none so a finger draws instead of scrolling the page;
 * the rest of the card scrolls as usual.
 *
 * NO BACKTICKS IN ANY COMMENT INSIDE A STYLED TEMPLATE in this file.
 */

/** The pad's CSS size; the canvas backing store is this times the DPR (cap 2). */
export const PAD_HEIGHT = 160;
export const PAD_MAX_WIDTH = 560;

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  width: 100%;
  max-width: ${PAD_MAX_WIDTH}px;
  min-width: 0;
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;
  min-width: 0;
`;

/* The typed signature as it will read on the document: the UI face, italic
   and light, a size up. A script-like look with no web font fetched. */
export const TypedPreview = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
  min-width: 0;
  min-height: ${space(9)};
  padding: ${spacing.xs} ${spacing.sm};
  border-bottom: 1px solid ${palette.line};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxl}px;
  font-style: italic;
  font-weight: ${typography.weight.light};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.tight};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const PadFrame = styled.div<{ $disabled: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${PAD_MAX_WIDTH}px;
  height: ${PAD_HEIGHT}px;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  overflow: hidden;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard};

  &:hover {
    border-color: ${({ $disabled }) =>
      $disabled ? palette.lineStrong : palette.teal};
  }
`;

/* touch-action none: the finger draws, the page does not scroll under it.
   WebKit still needs the non-passive touchmove listener the component adds. */
export const Canvas = styled.canvas<{ $disabled: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'crosshair')};
`;

export const PadFoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const Hint = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.regular};
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

/* The missing-signature line beside Clear: takes the row, wraps under it at
   320 rather than squeezing the button. */
export const FootHint = styled(Hint)`
  flex: 1 1 ${space(20)};
`;
